import React from "react";
import { Box, Button, IconButton, Tab, Tabs, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { CompareArrows, Description, DoneAll, EditNote } from "@mui/icons-material";
import { translationService } from "../../services/translation/TranslationService";
import { DetectionSource } from "./analysisDetections";

const t = (key: string) => translationService.translate(key);

interface DetectionSourceSelectorProps {
  value: DetectionSource;
  onChange(next: DetectionSource): void;
  /**
   * Label for the raw-output tab: "Inspection Data" on the Inspection Data Capture tab,
   * "Analysis output" on the Damage Detection tab. Same control, different machine behind it.
   */
  originalLabelKey: string;
  /** Whether anything is approved yet for this side. Gates Compare. */
  hasApproved: boolean;
  /** Approve every detection in the list in one action. Omitted, the button is not rendered. */
  onApproveAll?(): void;
  /** True while an approve-all is being persisted. */
  approving?: boolean;
  /** The graph and list for the chosen source, laid out beside the rail. */
  children?: React.ReactNode;
}

/**
 * Chooses which detections the Inspection Graph and the Detection List are built from.
 *
 * Both are driven by this one control on purpose: they describe the same inspection, so letting
 * them show different sources would make the pipe diagram disagree with the list under it.
 *
 * Drawn as a vertical rail beside the content rather than a second row of tabs: the page's
 * result tabs already run across the top, and two stacked tab rows read as clutter. The rail is
 * a workspace menu -- what the machine read, where the reviewer works, what the review changed.
 * Its names run vertically, so the rail is only as wide as one line of text and the graph keeps
 * the width it needs. On narrow screens it folds back to a normal row above the content.
 *
 * The machine output is the default: before a review it is the only list that has anything in
 * it. The workbench stays selectable even while empty — an inspection with nothing approved has
 * no reviewed results, and saying so is the point. Compare needs both sides, so it alone is gated.
 */
const DetectionSourceSelector: React.FC<DetectionSourceSelectorProps> = ({
  value,
  onChange,
  originalLabelKey,
  hasApproved,
  onApproveAll,
  approving,
  children,
}) => {
  const theme = useTheme();
  const vertical = useMediaQuery(theme.breakpoints.up("md"));
  const label = (key: string) => (
    <Box component="span" sx={vertical ? { writingMode: "vertical-rl", whiteSpace: "nowrap" } : undefined}>
      {t(key)}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "row" : "column",
        alignItems: vertical ? "flex-start" : "stretch",
        gap: 2,
      }}
    >
      {/* Only the rail is left out of the PDF; the content beside it is what gets exported. */}
      <Box
        data-html2canvas-ignore
        sx={{
          flexShrink: 0,
          width: vertical ? 52 : "auto",
          display: "flex",
          flexDirection: vertical ? "column" : "row",
          alignItems: vertical ? "stretch" : "center",
          flexWrap: "wrap",
          gap: 1.5,
          ...(vertical && { position: "sticky", top: 16 }),
        }}
      >
        <Tabs
          value={value}
          onChange={(_e, next) => onChange(next as DetectionSource)}
          orientation={vertical ? "vertical" : "horizontal"}
          variant={vertical ? "standard" : "scrollable"}
          scrollButtons="auto"
          TabIndicatorProps={{
            sx: vertical ? { left: 0, width: 4, borderRadius: "0 4px 4px 0" } : { height: 3 },
          }}
          sx={{
            minHeight: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            overflow: "hidden",
            "& .MuiTab-root": {
              minHeight: 0,
              minWidth: 0,
              py: vertical ? 2 : 1.5,
              px: vertical ? 0.5 : 2,
              gap: vertical ? 1 : undefined,
              justifyContent: vertical ? "center" : "flex-start",
              textAlign: "left",
              "& .MuiTab-iconWrapper": vertical ? { m: 0 } : undefined,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "text.secondary",
              transition: "background-color 120ms, color 120ms",
              "&:hover": { bgcolor: "action.hover", color: "text.primary" },
            },
            "& .MuiTab-root + .MuiTab-root": vertical
              ? { borderTop: 1, borderColor: "divider" }
              : { borderLeft: 1, borderColor: "divider" },
            "& .MuiTab-root.Mui-selected": {
              color: "primary.main",
              fontWeight: 700,
              bgcolor: "action.selected",
            },
            // Disabled still takes the pointer, so the tooltip saying why Compare is off can show.
            "& .MuiTab-root.Mui-disabled": { pointerEvents: "auto", cursor: "not-allowed" },
          }}
        >
          <Tab
            value="original"
            icon={<Description fontSize="small" />}
            iconPosition={vertical ? "top" : "start"}
            label={label(originalLabelKey)}
          />
          <Tab
            value="approved"
            icon={<EditNote fontSize="small" />}
            iconPosition={vertical ? "top" : "start"}
            label={label("comparison.detection-source.workbench")}
          />
          <Tab
            value="compare"
            disabled={!hasApproved}
            icon={<CompareArrows fontSize="small" />}
            iconPosition={vertical ? "top" : "start"}
            label={
              <Tooltip title={hasApproved ? "" : t("comparison.detection-source.compare-hint")}>
                {label("comparison.detection-source.compare-changes")}
              </Tooltip>
            }
          />
        </Tabs>

        {onApproveAll && (
          // The rail has no room for a worded button; the name moves into the tooltip.
          <Tooltip
            placement={vertical ? "right" : "bottom"}
            title={
              vertical
                ? `${t("comparison.detection-source.approve-all")} — ${t("comparison.detection-source.approve-all-hint")}`
                : t("comparison.detection-source.approve-all-hint")
            }
          >
            <span style={vertical ? { alignSelf: "center" } : undefined}>
              {vertical ? (
                <IconButton
                  color="success"
                  disabled={approving}
                  onClick={onApproveAll}
                  aria-label={t("comparison.detection-source.approve-all")}
                  sx={{ border: 1, borderColor: "success.main", borderRadius: 2 }}
                >
                  <DoneAll />
                </IconButton>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<DoneAll />}
                  disabled={approving}
                  onClick={onApproveAll}
                >
                  {t("comparison.detection-source.approve-all")}
                </Button>
              )}
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>{children}</Box>
    </Box>
  );
};

export default DetectionSourceSelector;
